/**
 * 15 §3.1 broadcast-bracket component sheet. Pure Canvas2D vector in virtual
 * px; no bitmaps. Register rules (09 §0): open L-corner brackets, never
 * boxes; neutral ink; team accent only when naming a team, always paired
 * with the team glyph (09 §3); dimension ticks on one arm are the single
 * sanctioned blueprint quote.
 */
import { AMBER, INK, TEAM_ACCENT } from '../fx/constants.js'
import { drawGlyph, type GlyphName } from '../glyphs.js'
import type { Team } from '../../../sim/types.js'

export const HUD_FONT_STACK = '"Barlow Condensed", "Oswald", "Arial Narrow", sans-serif'
export const MONO_FONT_STACK = '"IBM Plex Mono", ui-monospace, Menlo, Consolas, monospace'
export const INK_ALPHA = 0.85
export const DARK_OUTLINE = 'rgba(8,10,12,0.9)'

export type Corner = 'tl' | 'tr' | 'bl' | 'br'

/** Per-draw style state shared by every component (set once per frame). */
export interface HudStyle { highContrast: boolean; scale: number }
let style: HudStyle = { highContrast: false, scale: 1 }
export function setHudStyle(s: Partial<HudStyle>): void { style = { ...style, ...s } }
export function getHudStyle(): HudStyle { return style }

export function hudFont(size: number, weight: 500 | 700 = 500): string { return `${weight} ${Math.round(size)}px ${HUD_FONT_STACK}` }
export function monoFont(size: number): string { return `400 ${Math.round(size)}px ${MONO_FONT_STACK}` }
export const inkAlpha = (a: number): string => `rgba(232,236,239,${a})`
export const teamAccent = (team: Team): string => TEAM_ACCENT[team]
export const teamGlyph = (team: Team): GlyphName => (team === 0 ? 'teamA' : 'teamB')
export const teamLetter = (team: Team): string => (team === 0 ? 'A' : 'B')

/** Stroke the current path; under highContrast a dark wider pass goes first. */
export function strokeInk(ctx: CanvasRenderingContext2D, ink: string, width: number): void {
  if (style.highContrast) { ctx.save(); ctx.strokeStyle = DARK_OUTLINE; ctx.lineWidth = width + 2; ctx.stroke(); ctx.restore() }
  ctx.strokeStyle = ink; ctx.lineWidth = width; ctx.stroke()
}

/** Text with the optional dark outline. */
export function text(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, opts: { font: string; ink?: string; align?: CanvasTextAlign; baseline?: CanvasTextBaseline; alpha?: number } ): void {
  ctx.save()
  ctx.font = opts.font
  ctx.textAlign = opts.align ?? 'left'
  ctx.textBaseline = opts.baseline ?? 'alphabetic'
  if (opts.alpha !== undefined) ctx.globalAlpha *= opts.alpha
  if (style.highContrast) { ctx.strokeStyle = DARK_OUTLINE; ctx.lineWidth = 3; ctx.lineJoin = 'round'; ctx.strokeText(s, x, y) }
  ctx.fillStyle = opts.ink ?? inkAlpha(INK_ALPHA)
  ctx.fillText(s, x, y)
  ctx.restore()
}

/** Tabular digits: every glyph drawn at a fixed advance (canvas has no font-variant-numeric). */
export function tabularText(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, size: number, opts: { ink?: string; align?: CanvasTextAlign; weight?: 500 | 700 } = {}): number {
  const font = hudFont(size, opts.weight ?? 700)
  ctx.save(); ctx.font = font
  const adv = ctx.measureText('0').width * 1.05
  const narrow = ctx.measureText(':').width
  const widths = [...s].map((c) => (c === ':' || c === '.' ? narrow : adv))
  const total = widths.reduce((a, b) => a + b, 0)
  ctx.restore()
  let cx = opts.align === 'center' ? x - total / 2 : opts.align === 'right' ? x - total : x
  ;[...s].forEach((c, i) => { text(ctx, c, cx + widths[i] / 2, y, { font, ink: opts.ink, align: 'center' }); cx += widths[i] })
  return total
}

export interface BracketOpts { corners?: Corner[]; weight?: number; arm?: number; ticks?: number; progress?: number; ink?: string }

/** Open L-corner brackets. `progress` 0..1 pen-draws each arm from the
 *  corner outward (09 §0 draw-on 120 ms); never closes into a box. */
export function bracket(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, o: BracketOpts = {}): void {
  const corners = o.corners ?? ['tl', 'br']
  const arm = Math.min((o.arm ?? 18) * style.scale, w / 2, h / 2)
  const p = Math.max(0, Math.min(1, o.progress ?? 1))
  if (p <= 0) return
  const a = arm * p
  const ink = o.ink ?? inkAlpha(INK_ALPHA)
  ctx.beginPath()
  for (const c of corners) {
    const cx = c === 'tl' || c === 'bl' ? x : x + w
    const cy = c === 'tl' || c === 'tr' ? y : y + h
    const dx = c === 'tl' || c === 'bl' ? 1 : -1
    const dy = c === 'tl' || c === 'tr' ? 1 : -1
    ctx.moveTo(cx, cy + dy * a); ctx.lineTo(cx, cy); ctx.lineTo(cx + dx * a, cy)
    // Dimension ticks on the horizontal arm (the blueprint quote), 2–3 tiny ticks.
    const n = Math.min(3, Math.max(0, o.ticks ?? 0))
    if (n && p >= 1) {
      const pitch = arm / (n + 1)
      for (let i = 1; i <= n; i++) { const tx = cx + dx * pitch * i; ctx.moveTo(tx, cy); ctx.lineTo(tx, cy + dy * 3) }
    }
  }
  strokeInk(ctx, ink, (o.weight ?? 2) * Math.max(0.75, style.scale * 0.9))
}

export interface SegBarOpts { hatchBelow01?: number; dim?: boolean; flashOutline?: boolean; ink?: string }

/** Segmented bar: outlined segments, 2 px gaps, filled on threshold; the
 *  region below `hatchBelow01` is hatched. Returns the number filled. */
export function segBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, segments: number, value01: number, o: SegBarOpts = {}): number {
  const gap = 2
  const sw = (w - gap * (segments - 1)) / segments
  const ink = o.ink ?? inkAlpha(o.dim ? INK_ALPHA * 0.5 : INK_ALPHA)
  const filled = Math.max(0, Math.min(segments, Math.floor(value01 * segments + 1e-6)))
  ctx.save()
  for (let i = 0; i < segments; i++) {
    const sx = x + i * (sw + gap)
    ctx.beginPath(); ctx.rect(sx, y, sw, h)
    if (o.flashOutline) { strokeInk(ctx, ink, 1.5) }
    else if (i < filled) { ctx.fillStyle = ink; ctx.fill() }
    else strokeInk(ctx, inkAlpha(INK_ALPHA * 0.45), 1)
  }
  if (o.hatchBelow01 && o.hatchBelow01 > 0) {
    const hw = w * Math.min(1, o.hatchBelow01)
    ctx.beginPath(); ctx.rect(x, y, hw, h); ctx.clip()
    ctx.beginPath()
    for (let d = -h; d < hw + h; d += 4) { ctx.moveTo(x + d, y + h); ctx.lineTo(x + d + h, y) }
    ctx.strokeStyle = inkAlpha(0.5); ctx.lineWidth = 1; ctx.stroke()
  }
  ctx.restore()
  return filled
}

export type PipKind = 'held' | 'flight' | 'returning' | 'ground' | 'empty'

/** Disc pip icon (Z5): held filled ring, flight ring + motion ticks,
 *  returning dashed ring, ground dotted ring, empty ring. */
export function pip(ctx: CanvasRenderingContext2D, kind: PipKind, x: number, y: number, size: number, ink: string = inkAlpha(INK_ALPHA)): void {
  const r = size / 2 - 1
  ctx.save()
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2)
  switch (kind) {
    case 'held': ctx.fillStyle = ink; ctx.fill(); break
    case 'flight': strokeInk(ctx, ink, 1.5); ctx.beginPath(); for (let i = 0; i < 3; i++) { const yy = y - r * 0.6 + i * r * 0.6; ctx.moveTo(x - r - 2 - i, yy); ctx.lineTo(x - r - 5 - i, yy) } strokeInk(ctx, ink, 1); break
    case 'returning': ctx.setLineDash([3, 2]); strokeInk(ctx, ink, 1.5); break
    case 'ground': ctx.setLineDash([1, 2]); strokeInk(ctx, ink, 1.5); break
    case 'empty': strokeInk(ctx, inkAlpha(0.35), 1); break
  }
  ctx.restore()
}

export type ScoreCellState = 'empty' | 'won' | 'void' | 'current'

/** Z3 cell: won = team glyph in accent (A filled square / B open circle),
 *  void = × + V, current = bracketed, empty = 10×10 outline. */
export function scoreCell(ctx: CanvasRenderingContext2D, state: ScoreCellState, team: Team | null, x: number, y: number, size: number): void {
  const s = size
  ctx.save()
  if (state === 'won' && team !== null) {
    const accent = teamAccent(team)
    if (team === 0) { ctx.fillStyle = accent; ctx.fillRect(x - s / 2, y - s / 2, s, s) }
    else { ctx.beginPath(); ctx.arc(x, y, s / 2 - 1, 0, Math.PI * 2); strokeInk(ctx, accent, 2) }
  } else if (state === 'void') {
    ctx.beginPath(); ctx.moveTo(x - s / 2, y - s / 2); ctx.lineTo(x + s / 2, y + s / 2); ctx.moveTo(x + s / 2, y - s / 2); ctx.lineTo(x - s / 2, y + s / 2); strokeInk(ctx, inkAlpha(INK_ALPHA), 1.5)
    text(ctx, 'V', x, y + s + 2, { font: hudFont(s * 0.9), align: 'center', baseline: 'top' })
  } else {
    ctx.beginPath(); ctx.rect(x - s / 2, y - s / 2, s, s); strokeInk(ctx, inkAlpha(0.5), 1)
  }
  if (state === 'current') bracket(ctx, x - s, y - s, s * 2, s * 2, { corners: ['tl', 'br'], arm: 5, weight: 1.5 })
  ctx.restore()
}

/** Z2 timer text; `last10` = amber + tick pulse handled by caller scale. */
export function timer(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, size: number, o: { last10?: boolean; amber?: boolean } = {}): void {
  tabularText(ctx, s, x, y, size, { ink: o.amber || o.last10 ? AMBER : inkAlpha(INK_ALPHA), align: 'center' })
}

export interface FeedRowData { attacker: { name: string; team: Team } | null; victim: { name: string; team: Team }; tags: string[]; posthumous?: boolean; double?: boolean }

/** Z6 row: `[glyph] Thrower ⟶ [glyph] Victim TAGS`, right-aligned at `x`. */
export interface FeedPart { t: string; ink: string; glyph?: GlyphName; small?: boolean }
/** Row text parts, left→right (09 §1 Z6): `[glyph] Thrower ⟶ [glyph] Victim` then
 *  tags; a bank elimination shows a small `⤵` before `BANK`. Pure, for tests. */
export function feedParts(row: FeedRowData): FeedPart[] {
  const parts: FeedPart[] = []
  if (row.double) parts.push({ t: 'DOUBLE DEREZ', ink: inkAlpha(INK_ALPHA) })
  else {
    if (row.attacker) parts.push({ t: `${row.posthumous ? '† ' : ''}${row.attacker.name}`, ink: teamAccent(row.attacker.team), glyph: teamGlyph(row.attacker.team) }, { t: '⟶', ink: inkAlpha(INK_ALPHA) })
    parts.push({ t: row.victim.name, ink: teamAccent(row.victim.team), glyph: teamGlyph(row.victim.team) })
  }
  for (const tg of row.tags) {
    if (tg === 'BANK') parts.push({ t: '⤵', ink: inkAlpha(INK_ALPHA), small: true })
    parts.push({ t: tg, ink: tg === 'RESCINDED' ? AMBER : inkAlpha(INK_ALPHA) })
  }
  return parts
}

export function feedRow(ctx: CanvasRenderingContext2D, row: FeedRowData, xRight: number, y: number, o: { strike?: boolean; alpha?: number; scale?: number } = {}): void {
  const sc = o.scale ?? 1
  const fs = 14 * sc
  const font = hudFont(fs)
  ctx.save()
  ctx.globalAlpha *= o.alpha ?? 1
  ctx.font = font
  const parts = feedParts(row)
  const gap = 6 * sc, gs = fs
  let total = 0
  for (const p of parts) total += ctx.measureText(p.t).width + gap + (p.glyph ? gs + 3 : 0)
  let cx = xRight - total
  for (const p of parts) {
    if (p.glyph) { drawGlyph(ctx, p.glyph, cx + gs / 2, y - fs * 0.35, gs * 0.9, p.ink); cx += gs + 3 }
    text(ctx, p.t, cx, y, { font: p.small ? hudFont(fs * 0.8) : font, ink: p.ink })
    cx += ctx.measureText(p.t).width + gap
  }
  if (o.strike) { ctx.beginPath(); ctx.moveTo(xRight - total, y - fs * 0.35); ctx.lineTo(xRight - gap, y - fs * 0.35); strokeInk(ctx, inkAlpha(INK_ALPHA), 1.5) }
  ctx.restore()
}

/** Z7 call-out: pen reveal left→right via clip (`progress` 0..1), optional
 *  sub-line and team glyph + accent when naming a team. */
export function callout(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, size: number, o: { progress?: number; sub?: string; team?: Team | null } = {}): void {
  const p = Math.max(0, Math.min(1, o.progress ?? 1))
  if (p <= 0) return
  ctx.save()
  ctx.font = hudFont(size, 700)
  const w = ctx.measureText(s).width + (o.team !== null && o.team !== undefined ? size * 1.1 : 0)
  ctx.beginPath(); ctx.rect(x - w / 2 - 4, y - size * 1.2, (w + 8) * p, size * 2.4); ctx.clip()
  let tx = x
  if (o.team !== null && o.team !== undefined) {
    drawGlyph(ctx, teamGlyph(o.team), x - w / 2 + size * 0.45, y - size * 0.35, size * 0.8, teamAccent(o.team))
    tx = x + size * 0.55
    text(ctx, s, tx, y, { font: hudFont(size, 700), ink: teamAccent(o.team), align: 'center' })
  } else text(ctx, s, tx, y, { font: hudFont(size, 700), align: 'center' })
  if (o.sub) text(ctx, o.sub, x, y + size * 0.55, { font: hudFont(Math.max(14, size * 0.4)), align: 'center', alpha: 0.85 })
  ctx.restore()
}

/** Small bracketed label (`PROTECTED`, `SYNC`, accolades, `BOT`). Returns width. */
export function tag(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, tone: 'neutral' | 'amber' | 'team' = 'neutral', team?: Team, size = 12): number {
  ctx.save(); ctx.font = hudFont(size, 700)
  const w = ctx.measureText(s).width + 10
  const ink = tone === 'amber' ? AMBER : tone === 'team' && team !== undefined ? teamAccent(team) : inkAlpha(INK_ALPHA)
  bracket(ctx, x, y - size, w, size + 6, { corners: ['tl', 'br'], arm: 5, weight: 1, ink })
  text(ctx, s, x + 5, y, { font: hudFont(size, 700), ink })
  ctx.restore()
  return w
}

export type CardState = 'idle' | 'in-deck' | 'locked' | 'revealed'

/** 06 §4 lobby deck card, 160×200 (draw only). */
export function card(ctx: CanvasRenderingContext2D, glyph: GlyphName, name: string, buff: string, nerf: string, state: CardState, x: number, y: number): void {
  const w = 160, h = 200
  ctx.save()
  ctx.fillStyle = state === 'idle' ? 'rgba(11,13,16,0.6)' : 'rgba(11,13,16,0.85)'
  ctx.fillRect(x, y, w, h)
  bracket(ctx, x, y, w, h, { corners: state === 'idle' ? ['tl', 'br'] : ['tl', 'tr', 'bl', 'br'], weight: state === 'locked' || state === 'revealed' ? 2.5 : 1.5, ticks: state === 'revealed' ? 3 : 0 })
  drawGlyph(ctx, glyph, x + w / 2, y + 64, 64)
  text(ctx, name.toUpperCase(), x + w / 2, y + 128, { font: hudFont(18, 700), align: 'center' })
  text(ctx, `+ ${buff}`, x + 10, y + 152, { font: monoFont(10), alpha: 0.8 })
  text(ctx, `− ${nerf}`, x + 10, y + 168, { font: monoFont(10), alpha: 0.8 })
  if (state === 'locked') tag(ctx, 'LOCKED', x + 10, y + 192, 'neutral')
  if (state === 'in-deck') tag(ctx, 'IN DECK', x + 10, y + 192, 'neutral')
  ctx.restore()
}

export interface RowCol { text: string; x: number; align?: CanvasTextAlign; ink?: string; font?: string; glyph?: GlyphName; tag?: 'neutral' | 'amber' | 'team'; team?: Team }

/** Results table row with pen reveal (clip left→right). */
export function row(ctx: CanvasRenderingContext2D, cols: RowCol[], x: number, y: number, o: { progress?: number; width?: number; size?: number } = {}): void {
  const p = Math.max(0, Math.min(1, o.progress ?? 1))
  if (p <= 0) return
  const size = o.size ?? 16
  ctx.save()
  ctx.beginPath(); ctx.rect(x - 4, y - size * 1.2, (o.width ?? 900) * p, size * 1.8); ctx.clip()
  for (const c of cols) {
    if (c.glyph) { drawGlyph(ctx, c.glyph, x + c.x, y - size * 0.35, size * 1.1, c.ink); continue }
    if (c.tag) { tag(ctx, c.text, x + c.x, y, c.tag, c.team, size * 0.75); continue }
    text(ctx, c.text, x + c.x, y, { font: c.font ?? hudFont(size), align: c.align ?? 'left', ink: c.ink })
  }
  ctx.restore()
}

export { INK, AMBER }
